/**
 * @file PickupPointsFilters.tsx
 * @description Панель фильтров раздела «Пункты выдачи» (phase19.12).
 *
 * После реальной синхронизации в кэше тысячи населённых пунктов, поэтому
 * фильтр по НП — не выпадающий список, а строка поиска: значение уходит в
 * серверный параметр `city` (contains/insensitive). Второй инпут ищет уже по
 * загруженной выдаче (адрес/код). Контракт value/onChange — как в остальных
 * формах модуля.
 */

import { List, Loader2, Map as MapIcon, Search } from "lucide-react";

import { Input } from "../ui/input";
import { Label } from "../ui/label";
import type { CarrierKind } from "../../lib/api-shop-carriers";

/** Значение фильтра «Перевозчик» = все. */
export const ALL_CARRIERS = "__all__";

export type PickupPointsView = "list" | "map";

export interface PickupPointsFilterState {
  /** Локальный поиск по уже загруженным точкам: адрес или код. */
  query: string;
  /** Серверный поиск по названию населённого пункта. */
  city: string;
  carrier: string;
  cashOnly: boolean;
  view: PickupPointsView;
}

interface Props {
  value: PickupPointsFilterState;
  onChange: (patch: Partial<PickupPointsFilterState>) => void;
  carriers: CarrierKind[];
  /** Идёт серверный запрос по новому населённому пункту. */
  cityLoading: boolean;
  filteredCount: number;
  loadedCount: number;
  /** Сервер вернул полную страницу — часть точек осталась за кадром. */
  truncated: boolean;
  serverLimit: number;
}

const VIEWS = [
  { key: "list", label: "Списком", Icon: List },
  { key: "map", label: "Картой", Icon: MapIcon },
] as const;

export function PickupPointsFilters({
  value,
  onChange,
  carriers,
  cityLoading,
  filteredCount,
  loadedCount,
  truncated,
  serverLimit,
}: Props) {
  return (
    <div className="flex flex-col gap-3">
      {/* На 360px оба поля идут в столбик, с sm — в две колонки. */}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-on-surface-variant" />
          <Input
            value={value.city}
            onChange={(e) => onChange({ city: e.target.value })}
            placeholder="Населённый пункт…"
            aria-label="Поиск по населённому пункту"
            className="pl-9"
          />
          {cityLoading && (
            <Loader2 className="absolute right-3 top-1/2 size-4 -translate-y-1/2 animate-spin text-on-surface-variant" />
          )}
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-on-surface-variant" />
          <Input
            value={value.query}
            onChange={(e) => onChange({ query: e.target.value })}
            placeholder="Адрес или код пункта…"
            aria-label="Поиск по адресу или коду пункта"
            className="pl-9"
          />
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex min-w-[160px] flex-1 flex-col gap-1.5 sm:flex-none">
          <Label htmlFor="carrier">Перевозчик</Label>
          <select
            id="carrier"
            value={value.carrier}
            onChange={(e) => onChange({ carrier: e.target.value })}
            className="h-11 rounded-md border border-on-surface/20 bg-surface-lowest px-3 text-sm"
          >
            <option value={ALL_CARRIERS}>Все</option>
            {carriers.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <label className="flex min-h-11 items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={value.cashOnly}
            onChange={(e) => onChange({ cashOnly: e.target.checked })}
          />
          Только с наличными
        </label>
        <div className="ml-auto flex gap-1.5">
          {VIEWS.map(({ key, label, Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => onChange({ view: key })}
              aria-pressed={value.view === key}
              className={`inline-flex h-11 items-center gap-1.5 rounded-full px-4 text-sm transition-colors ${
                value.view === key
                  ? "bg-primary font-medium text-on-primary"
                  : "bg-surface-low text-on-surface-variant hover:bg-accent"
              }`}
            >
              <Icon className="size-4" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Честный счётчик: сервер отдаёт не больше serverLimit точек за раз. */}
      <div className="text-xs text-on-surface-variant">
        {truncated
          ? `Показаны первые ${serverLimit} — уточните населённый пункт. На экране: ${filteredCount}`
          : `Найдено пунктов: ${filteredCount} из ${loadedCount}`}
      </div>
    </div>
  );
}
